import { automationsRename } from './automations';
import { broadcastsRename } from './broadcasts';
import { messageActivityRename } from './message-activity';
import { profileActivityRename } from './profile-activity';
import { profilesRename } from './profiles';
import { reportsRename } from './reports';
import type { ProductTaxonomyRenameDefinition } from '../types';

const productTaxonomyRenameDefinitions = {
  automations: automationsRename,
  broadcasts: broadcastsRename,
  messageActivity: messageActivityRename,
  profileActivity: profileActivityRename,
  profiles: profilesRename,
  reports: reportsRename,
} as const;

type ProductTaxonomyRenameCatalogKey = keyof ProductTaxonomyRenameDefinition;
type ProductTaxonomyRenameCatalog<Key extends ProductTaxonomyRenameCatalogKey> = NonNullable<
  ProductTaxonomyRenameDefinition[Key]
>;
type ProductTaxonomyRenameCatalogEntries<Key extends ProductTaxonomyRenameCatalogKey> = UnionToIntersection<
  (typeof productTaxonomyRenameDefinitions)[keyof typeof productTaxonomyRenameDefinitions] extends infer Rename
    ? Rename extends { [Property in Key]: infer Entries }
      ? Entries
      : Record<never, never>
    : never
>;
type UnionToIntersection<Union> = (Union extends unknown ? (value: Union) => void : never) extends (
  value: infer Intersection,
) => void
  ? Intersection
  : never;

function collectProductTaxonomyCatalog<Key extends ProductTaxonomyRenameCatalogKey>(
  key: Key,
): ProductTaxonomyRenameCatalogEntries<Key> {
  const catalog: ProductTaxonomyRenameCatalog<Key> = {};

  for (const rename of Object.values(productTaxonomyRenameDefinitions)) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const entries = rename[key as keyof typeof rename] as unknown as ProductTaxonomyRenameCatalog<Key> | undefined;

    if (entries != null) {
      Object.assign(catalog, entries);
    }
  }

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return catalog as ProductTaxonomyRenameCatalogEntries<Key>;
}

export const productTaxonomyTermCatalog = collectProductTaxonomyCatalog('terms');
export const productTaxonomyCopyCatalog = collectProductTaxonomyCatalog('copy');
export const productTaxonomyRouteCatalog = collectProductTaxonomyCatalog('routes');
export const productTaxonomySideNavItemIdMap = collectProductTaxonomyCatalog('sideNavItemIds');
